import { useState, useEffect } from 'react';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Building2, MapPin, Plus, Edit } from 'lucide-react';
import { toast } from 'sonner';

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', location: '' });

  const fetchBranches = async () => {
    try {
      const res = await api.get('/branches');
      setBranches(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBranches(); }, []);

  const handleSave = async () => {
    try {
      if (editingId) {
        await api.put(`/branches/${editingId}`, form);
        toast.success('Branch updated');
      } else {
        await api.post('/branches', form);
        toast.success('Branch created');
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ name: '', location: '' });
      fetchBranches();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleEdit = (branch) => {
    setEditingId(branch.id);
    setForm({ name: branch.name, location: branch.location });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="branches-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Branches</h1>
          <p className="text-neutral-500 text-sm mt-1">{branches.length} branches across Kerala</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="add-branch-button"
          onClick={() => { setEditingId(null); setForm({ name: '', location: '' }); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" /> Add Branch
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-neutral-200 rounded animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map(branch => (
            <Card key={branch.id} className="border border-neutral-200 shadow-sm rounded-md hover:shadow-md transition-all duration-200 hover:-translate-y-[1px]"
              data-testid={`branch-card-${branch.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-50 rounded-sm flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-950">{branch.name}</h3>
                      <p className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" /> {branch.location}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(branch)}
                    data-testid={`edit-branch-${branch.id}`}>
                    <Edit className="h-3.5 w-3.5 text-neutral-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="branch-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editingId ? 'Edit Branch' : 'Add Branch'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Branch Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="rounded-sm" data-testid="branch-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Location</Label>
              <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                className="rounded-sm" data-testid="branch-location-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="branch-save-button">
              {editingId ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
